// The browser keys that belong to ONE signed-in identity, and the three
// operations that keep them from crossing between people on a shared browser.
//
// Three values are held in `localStorage` on behalf of the person at the
// keyboard: the unsent composer draft, and the two deep-link stashes (`?q=`
// text and the `/r/<cid>` report target). The draft outlives a session on
// purpose — a 401 mid-conversation preserves it so it rehydrates after the
// user logs back in (App.tsx `handleSessionExpired`) — and that same
// persistence is what would otherwise hand it to whoever signs in next.
//
// So each of the three is stored under a key that names the identity it
// belongs to, and a marker records which identity this browser last served:
//
//   claimIdentity(userId)    at the first authenticated render — claims the
//                            pre-identity captures, or drops everything the
//                            previous identity left behind.
//   draftKeyFor(userId) etc. the per-identity key each value is read/written
//                            under.
//   releaseIdentity(userId)  at sign-out — removes all three plus the marker.
//
// The two deep-link stashes are captured at module load, before React mounts
// and before `/me` has said who (if anyone) is signed in, so they cannot be
// keyed at capture. They are instead CLAIMED into the identity's own key at
// the first authenticated render, and dropped untouched when the identity
// signing in is not the one this browser last served. A deep link opened by
// the previous person therefore never prefills the next person's composer;
// the cost is that a link opened on a browser whose last identity did not sign
// out is dropped rather than guessed at, and the visitor retypes.
//
// Pure functions with the storage injected, like pendingQ.ts, so all of this
// is unit-testable without rendering the App.

// Which identity this browser last served. Written by `claimIdentity`, removed
// by `releaseIdentity`; absent on a browser no one has signed in on yet.
export const IDENTITY_KEY = 'dsh_identity';

// The per-identity prefixes. Every key carrying one is dropped wholesale when
// the identity changes, so adding a prefix here is enough to enrol a value.
export const DRAFT_PREFIX = 'dsh_chat_draft_';
export const PENDING_Q_PREFIX = 'dsh_pending_q_';
export const PENDING_R_PREFIX = 'dsh_pending_r_';
const SCOPED_PREFIXES = [DRAFT_PREFIX, PENDING_Q_PREFIX, PENDING_R_PREFIX];

// The pre-identity capture keys — written at module load by the `?q=` and
// `/r/<cid>` capture, before any identity is known.
export const PENDING_Q_CAPTURE_KEY = 'dsh_pending_q';
export const PENDING_R_CAPTURE_KEY = 'dsh_pending_r';

// The draft key from before drafts were keyed by identity. Nothing reads it
// any more, so a browser that has one is holding someone's unsent text with no
// way left to reach or clear it — it is swept on the next claim and sign-out
// alike. Removable once no live browser can still be carrying one.
export const LEGACY_DRAFT_KEY = 'dsh_chat_draft';

export const draftKeyFor = (userId: string): string => DRAFT_PREFIX + userId;
export const pendingQKeyFor = (userId: string): string => PENDING_Q_PREFIX + userId;
export const pendingRKeyFor = (userId: string): string => PENDING_R_PREFIX + userId;

// The slice of the Storage interface these functions use. `key`/`length` are
// needed to enumerate what a departed identity left behind.
export type IdentityStorage = Pick<
  Storage,
  'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'
>;

// Every key currently held under one of the per-identity prefixes. Snapshotted
// before any removal — removing while indexing would skip entries.
const scopedKeys = (storage: IdentityStorage): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      keys.push(key);
    }
  }
  return keys;
};

// Move a pre-identity capture under the identity's own key. A capture that is
// absent or blank is simply cleared — there is nothing to claim.
const claimCapture = (
  captureKey: string,
  scopedKey: string,
  storage: IdentityStorage,
): void => {
  const captured = storage.getItem(captureKey);
  storage.removeItem(captureKey);
  if (captured && captured.trim()) storage.setItem(scopedKey, captured);
};

/**
 * Claim this browser for `userId`. Call once, synchronously, BEFORE anything
 * reads a per-identity key.
 *
 * Same identity as this browser last served (or a browser no one has signed in
 * on yet): the two pre-identity captures are claimed into that identity's keys,
 * and an existing draft is left standing — this is the session-expiry round
 * trip, where the whole point is that the unsent text comes back.
 *
 * A different identity: every value the previous one left under a per-identity
 * prefix is dropped, along with the two unclaimed captures, before this session
 * reads anything.
 *
 * SSR / disabled storage safe — any throw is swallowed (no-op).
 */
export function claimIdentity(
  userId: string,
  storage: IdentityStorage = localStorage,
): void {
  try {
    storage.removeItem(LEGACY_DRAFT_KEY);
    const prior = storage.getItem(IDENTITY_KEY);
    if (prior !== null && prior !== userId) {
      for (const key of scopedKeys(storage)) storage.removeItem(key);
      storage.removeItem(PENDING_Q_CAPTURE_KEY);
      storage.removeItem(PENDING_R_CAPTURE_KEY);
    } else {
      claimCapture(PENDING_Q_CAPTURE_KEY, pendingQKeyFor(userId), storage);
      claimCapture(PENDING_R_CAPTURE_KEY, pendingRKeyFor(userId), storage);
    }
    storage.setItem(IDENTITY_KEY, userId);
  } catch {
    /* SSR / disabled storage — ignore */
  }
}

/**
 * Sign-out: remove everything this identity held, and the marker with it, so
 * the browser is left as it was found and the next person starts clean. The
 * two pre-identity captures go too — an unclaimed one was stashed during this
 * identity's visit.
 *
 * SSR / disabled storage safe — any throw is swallowed (no-op).
 */
export function releaseIdentity(
  userId: string,
  storage: Pick<Storage, 'removeItem'> = localStorage,
): void {
  try {
    storage.removeItem(draftKeyFor(userId));
    storage.removeItem(pendingQKeyFor(userId));
    storage.removeItem(pendingRKeyFor(userId));
    storage.removeItem(PENDING_Q_CAPTURE_KEY);
    storage.removeItem(PENDING_R_CAPTURE_KEY);
    storage.removeItem(LEGACY_DRAFT_KEY);
    storage.removeItem(IDENTITY_KEY);
  } catch {
    /* SSR / disabled storage — ignore */
  }
}
